import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Box, Chip, Divider, Paper, Stack, Typography, Unstable_Grid2 as Grid,} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Button from '../../../../../../components/base/button/button';
import Form, {MuiRFF, Yup} from '../../../../../../components/form';
import WalletAutocomplete, {resolveWalletId} from '../../../../../../components/text-fields/autocomplete/wallet-autocomplete';
import {useConfigureNotebook} from '../../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../../hooks/use-behavioural-analytics-tool';
import useAccessLevels from '../../../../../../hooks/use-access-levels';
import useIsMounted from '../../../../../../hooks/use-is-mounted';
import PageHeader from '../../components/page-header';
import {mockBizConfigs} from '../../mock-data';

const AdminWalletOperationsView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.admin.walletOperations'});
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const {configureNotebookTags} = useConfigureNotebook('biz-point-admin-wallet-operations');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const canAdjust = packages.bizPoint.adminManualAdjustment;
    const canSuspend = packages.bizPoint.adminWalletSuspend;

    const [adjustSuccess, setAdjustSuccess] = useState(false);
    const [adjustSubmitting, setAdjustSubmitting] = useState(false);
    const [statusResult, setStatusResult] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [statusSubmitting, setStatusSubmitting] = useState(false);
    const statusActionRef = useRef(null);

    /**
     * Configures notebook tags and behavioral analytics for wallet operations.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.bizPointAdminWalletOperations);
        configureBehavioralTool((tags) => tags.bizPointAdminWalletOperations);
    }, [configureBehavioralTool, configureNotebookTags]);

    const walletRequiredSchema = useMemo(() => Yup.mixed()
        .nullable()
        .test('wallet-required', t('adjust.walletRequired'), (value) => Boolean(resolveWalletId(value))), [t]);

    const adjustSchema = useMemo(() => Yup.object().shape({
        wallet: walletRequiredSchema,
        business: Yup.string().required(t('adjust.businessRequired')),
        amount: Yup.number()
            .typeError(t('adjust.amountRequired'))
            .required(t('adjust.amountRequired'))
            .notOneOf([0], t('adjust.amountNonZero')),
        reason: Yup.string().trim().required(t('adjust.reasonRequired')),
    }), [t, walletRequiredSchema]);

    const statusSchema = useMemo(() => Yup.object().shape({
        wallet: Yup.mixed()
            .nullable()
            .test('wallet-required', t('status.walletRequired'), (value) => Boolean(resolveWalletId(value))),
        reason: Yup.string().trim().required(t('status.reasonRequired')),
    }), [t]);

    const adjustValidate = useMemo(() => MuiRFF.makeValidateSync(adjustSchema), [adjustSchema]);
    const adjustRequired = useMemo(() => MuiRFF.makeRequired(adjustSchema), [adjustSchema]);
    const statusValidate = useMemo(() => MuiRFF.makeValidateSync(statusSchema), [statusSchema]);
    const statusRequired = useMemo(() => MuiRFF.makeRequired(statusSchema), [statusSchema]);

    const adjustInitialValues = useMemo(() => ({
        wallet: null,
        business: '',
        amount: '',
        reason: '',
    }), []);

    const statusInitialValues = useMemo(() => ({
        wallet: null,
        reason: '',
    }), []);

    /**
     * Posts a mock ledger adjustment.
     */
    const handleAdjustSubmit = useCallback(async (values, form) => {
        setAdjustSubmitting(true);
        setAdjustSuccess(false);
        // TODO: Replace with real API when endpoint is available
        const response = await Promise.resolve({resultFlag: true});
        if (!isMounted()) {
            return;
        }
        setAdjustSubmitting(false);
        if (response?.resultFlag) {
            setAdjustSuccess(true);
            form.restart(adjustInitialValues);
        }
    }, [adjustInitialValues, isMounted]);

    /**
     * Suspends or reinstates a wallet with a mandatory reason.
     */
    const handleStatusSubmit = useCallback(async (values, form) => {
        const action = statusActionRef.current;
        if (action !== 'suspend' && action !== 'reinstate') {
            return;
        }
        const walletId = resolveWalletId(values.wallet);
        setStatusSubmitting(true);
        setStatusMessage(null);
        // TODO: Replace with real API when endpoint is available
        const response = await Promise.resolve({resultFlag: true});
        if (!isMounted()) {
            return;
        }
        setStatusSubmitting(false);
        if (response?.resultFlag) {
            setStatusResult({wallet: walletId, suspended: action === 'suspend'});
            setStatusMessage(action === 'suspend' ? t('status.suspendedMessage') : t('status.reinstatedMessage'));
            form.change('reason', '');
        }
        statusActionRef.current = null;
    }, [isMounted, t]);

    const businessOptions = useMemo(
        () => mockBizConfigs.map((b) => ({label: b.business, value: b.business})),
        [],
    );

    if (!canAdjust && !canSuspend) {
        return null;
    }

    return (
        <Box>
            <PageHeader title={t('title')}/>

            <Grid container spacing={3}>
                {canAdjust && (
                    <Grid xs={12} md={canSuspend ? 6 : 12}>
                        <Paper variant="outlined" sx={{p: 3, height: '100%'}}>
                            <Stack spacing={1} sx={{mb: 2.5}}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {t('adjust.heading')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('adjust.description')}
                                </Typography>
                            </Stack>

                            <Alert
                                severity="info"
                                variant="outlined"
                                sx={{mb: 2.5, borderColor: 'info.main', borderWidth: 1}}
                            >
                                {t('adjust.infoNote')}
                            </Alert>

                            {adjustSuccess && (
                                <Alert severity="success" sx={{mb: 2}} onClose={() => setAdjustSuccess(false)}>
                                    {t('adjust.successMessage')}
                                </Alert>
                            )}

                            <Form
                                initialValues={adjustInitialValues}
                                validate={adjustValidate}
                                onSubmit={handleAdjustSubmit}
                                formProps={{className: 'w-100'}}
                            >
                                {() => (
                                    <Stack spacing={2.5}>
                                        <WalletAutocomplete
                                            form
                                            name="wallet"
                                            required={adjustRequired.wallet}
                                            fullWidth
                                        />
                                        <MuiRFF.Select
                                            name="business"
                                            label={t('adjust.business')}
                                            required={adjustRequired.business}
                                            data={businessOptions}
                                            formControlProps={{fullWidth: true}}
                                        />
                                        <MuiRFF.TextField
                                            name="amount"
                                            label={t('adjust.amount')}
                                            type="number"
                                            fullWidth
                                            helperText={t('adjust.amountHelper')}
                                            required={adjustRequired.amount}
                                        />
                                        <MuiRFF.TextField
                                            name="reason"
                                            label={t('adjust.reason')}
                                            fullWidth
                                            multiline
                                            rows={3}
                                            required={adjustRequired.reason}
                                        />
                                        <Stack direction="row" justifyContent="center">
                                            <Button type="submit" variant="contained" disabled={adjustSubmitting} size={'medium'}>
                                                {t('adjust.submit')}
                                            </Button>
                                        </Stack>
                                    </Stack>
                                )}
                            </Form>
                        </Paper>
                    </Grid>
                )}

                {canSuspend && (
                    <Grid xs={12} md={canAdjust ? 6 : 12}>
                        <Paper variant="outlined" sx={{p: 3, height: '100%'}}>
                            <Stack spacing={1} sx={{mb: 2.5}}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {t('status.heading')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('status.description')}
                                </Typography>
                            </Stack>

                            <Alert
                                severity="info"
                                variant="outlined"
                                sx={{mb: 2.5, borderColor: 'info.main', borderWidth: 1}}
                            >
                                {t('status.infoNote')}
                            </Alert>

                            {statusMessage && (
                                <Alert severity="success" sx={{mb: 2}} onClose={() => setStatusMessage(null)}>
                                    {statusMessage}
                                </Alert>
                            )}

                            <Form
                                initialValues={statusInitialValues}
                                validate={statusValidate}
                                onSubmit={handleStatusSubmit}
                                formProps={{className: 'w-100'}}
                            >
                                {({handleSubmit}) => (
                                    <Stack spacing={2.5}>
                                        <WalletAutocomplete
                                            form
                                            name="wallet"
                                            required={statusRequired.wallet}
                                            fullWidth
                                        />

                                        {statusResult && (
                                            <Stack
                                                direction="row"
                                                spacing={1.5}
                                                alignItems="center"
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    border: 1,
                                                    borderColor: statusResult.suspended ? 'error.main' : 'success.main',
                                                    bgcolor: 'background.paper',
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('status.currentLabel', {wallet: statusResult.wallet})}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    color={statusResult.suspended ? 'error' : 'success'}
                                                    label={statusResult.suspended
                                                        ? t('status.currentStatusSuspended')
                                                        : t('status.currentStatusActive')}
                                                />
                                            </Stack>
                                        )}

                                        <Divider/>

                                        <MuiRFF.TextField
                                            name="reason"
                                            label={t('status.reason')}
                                            fullWidth
                                            multiline
                                            rows={3}
                                            helperText={t('status.reasonHelper')}
                                            required={statusRequired.reason}
                                        />

                                        <Stack direction="row" spacing={1.5} justifyContent="center">
                                            <Button
                                                type="button"
                                                variant="contained"
                                                size={'medium'}
                                                color="error"
                                                disabled={statusSubmitting}
                                                onClick={() => {
                                                    statusActionRef.current = 'suspend';
                                                    handleSubmit();
                                                }}
                                            >
                                                {t('status.suspend')}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outlined"
                                                color="success"
                                                size={'medium'}
                                                disabled={statusSubmitting}
                                                onClick={() => {
                                                    statusActionRef.current = 'reinstate';
                                                    handleSubmit();
                                                }}
                                            >
                                                {t('status.reinstate')}
                                            </Button>
                                        </Stack>
                                    </Stack>
                                )}
                            </Form>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default AdminWalletOperationsView;
